import {useMutation, useQueryClient} from '@github-ui/react-query'
import type {UnhydratedTask, OptimisticTask} from '../types/task'
import type {CreateAPITaskParams, APITask, CreateAPITaskMutationParams} from '../types/api-task'
import {fetchCAPI} from '../utils/fetch-capi'
import {sortTasksByDate, transformAPITaskToUnhydrated} from '../utils/task-helpers'
import {uuid} from '../utils/uuid'
import {repoTasksApiPath} from '../utils/api-url-helpers'
import {sendEvent} from '@github-ui/hydro-analytics'
import {intentDetectionHeaders} from '../utils/intent-detection-headers'

interface CreateAPITaskResponse {
  task: APITask
}

interface CreateAPITaskMutationResult {
  task: APITask
  clientGeneratedId: string
}

export type FlatTaskList = Array<UnhydratedTask | OptimisticTask>

export interface UseCreateAPITaskOptions<TQueryData> {
  onError?: (error: Error, variables: CreateAPITaskMutationParams) => void

  /** Query key to use for optimistic updates. */
  queryKey: readonly unknown[]
  /**
   * We don't know the shape of the cached query data, but we need to update the
   * list of tasks for optimistic updates, errors, etc. This function extracts a
   * flat list of tasks from the cached query data.
   */
  getFlatTaskList: (data: TQueryData | undefined) => FlatTaskList | undefined
  /**
   * We don't know the shape of the cached query data, but we need to update the
   * list of tasks for optimistic updates, errors, etc. This function returns a
   * new version of the cached query data, updated with the provided flat list of tasks.
   */
  setFlatTaskList: (oldData: TQueryData | undefined, newList: FlatTaskList) => TQueryData
  /**
   * The page size for pagination. Used to truncate the list after replacing
   * the optimistic task with the real one which prevents task overflow.
   */
  pageSize: number
  /** Whether decoupled mode is enabled. */
  decoupledModeEnabled: boolean
}

export function useCreateAPITask<TQueryData>({
  onError,
  queryKey,
  getFlatTaskList,
  setFlatTaskList,
  pageSize,
  decoupledModeEnabled,
}: UseCreateAPITaskOptions<TQueryData>) {
  const queryClient = useQueryClient()

  const createTaskMutation = useMutation<CreateAPITaskMutationResult, Error, CreateAPITaskMutationParams>({
    mutationKey: ['createAPITask'],
    onMutate: async (params: CreateAPITaskMutationParams) => {
      // Cancel any existing refetches
      await queryClient.cancelQueries({queryKey})

      // Snapshot the previous value for potential rollback
      const previousTasks = queryClient.getQueryData<TQueryData>(queryKey)

      // Add optimistic task to cache
      const optimisticTask = createOptimisticTask(params)
      const newTasks = setFlatTaskList(previousTasks, [optimisticTask, ...(getFlatTaskList(previousTasks) ?? [])])
      queryClient.setQueryData(queryKey, newTasks)

      return {previousTasks}
    },
    mutationFn: async (params: CreateAPITaskMutationParams): Promise<CreateAPITaskMutationResult> => {
      sendEvent('copilot.agent_session.job_creation_requested', {
        event_identifiers: params.clientGeneratedId,
        event_type: params.eventType,
        nwo: params.nwo,
      })

      // If decoupled mode is enabled, default to not create PRs unless explicitly specified
      if (decoupledModeEnabled && params.createPullRequest === undefined) {
        params.createPullRequest = false
      }

      const response = await createTask(params)

      if (!response.ok) {
        sendEvent('copilot.agent_session.job_creation_failed', {
          event_identifiers: params.clientGeneratedId,
          event_type: params.eventType,
          nwo: params.nwo,
          status: response.status,
          error: response.statusText,
        })
        throw new Error(`Failed to create task: ${params.clientGeneratedId}`)
      }

      const data: CreateAPITaskResponse = await response.json()

      sendEvent('copilot.agent_session.job_creation_completed', {
        event_identifiers: params.clientGeneratedId,
        event_type: params.eventType,
        nwo: params.nwo,
        task_id: data.task.id,
      })

      return {task: data.task, clientGeneratedId: params.clientGeneratedId}
    },
    onError: (error, variables) => {
      // Only remove the specific failed task, there could be many in flight at the same time
      const currentTasks = queryClient.getQueryData<TQueryData>(queryKey)
      const tasksWithoutFailed = setFlatTaskList(
        currentTasks,
        (getFlatTaskList(currentTasks) ?? []).filter(task => task.clientGeneratedId !== variables.clientGeneratedId),
      )
      queryClient.setQueryData(queryKey, tasksWithoutFailed)

      onError?.(error, variables)
    },
    onSuccess: ({task, clientGeneratedId}) => {
      // Replace the optimistic task with the real server task.
      const transformedTask = transformAPITaskToUnhydrated(task)
      queryClient.setQueryData<TQueryData>(queryKey, oldData => {
        const cachedTasks = getFlatTaskList(oldData) ?? []
        const withoutOptimistic = cachedTasks.filter(t => t.clientGeneratedId !== clientGeneratedId)
        const sorted = transformedTask ? sortTasksByDate([transformedTask, ...withoutOptimistic]) : withoutOptimistic
        // Truncate to page size to prevent overflow
        const truncated = sorted.slice(0, pageSize)
        return setFlatTaskList(oldData, truncated)
      })

      queryClient.invalidateQueries({queryKey, exact: true})
    },
  })

  const createAPITask = (params: CreateAPITaskParams, clientTaskId?: string) => {
    const clientGeneratedId = clientTaskId ?? uuid()
    createTaskMutation.mutate({...params, clientGeneratedId})
  }

  const createAPITaskAsync = async (
    params: CreateAPITaskParams,
    clientTaskId?: string,
  ): Promise<CreateAPITaskMutationResult> => {
    const clientGeneratedId = clientTaskId ?? uuid()
    return createTaskMutation.mutateAsync({...params, clientGeneratedId})
  }

  return {
    createTask: createAPITask,
    createTaskAsync: createAPITaskAsync,
    isCreating: createTaskMutation.isPending,
    createError: createTaskMutation.error,
  }
}

/**
 * Helper function to create task via the Tasks API
 */
export async function createTask({
  nwo,
  problemStatement,
  model,
  agentId,
  customAgent,
  baseRef,
  createPullRequest,
  intentDetectionMode,
  eventType,
  eventContent,
  eventIdentifiers,
  clientGeneratedId,
}: CreateAPITaskMutationParams): Promise<Response> {
  const endpoint = repoTasksApiPath(nwo)

  // Convert agentId string to number for API
  let agentIdNumber: number | undefined = undefined
  if (agentId !== undefined && agentId !== null) {
    agentIdNumber = parseInt(agentId, 10)
    if (isNaN(agentIdNumber)) {
      throw new Error(`Invalid agentId: "${agentId}". agentId must be a valid numeric string.`)
    }
  }

  const allEventIdentifiers = [...(eventIdentifiers || []), clientGeneratedId]
  const additionalHeaders = intentDetectionHeaders(intentDetectionMode)
  return await fetchCAPI({
    path: endpoint,
    method: 'POST',
    additionalHeaders,
    body: {
      problem_statement: problemStatement,
      event_identifiers: allEventIdentifiers,
      ...(model && {model}),
      ...(agentIdNumber && {agent_id: agentIdNumber}),
      ...(customAgent && {custom_agent: customAgent}),
      ...(baseRef && {base_ref: baseRef}),
      ...(createPullRequest !== undefined && {create_pull_request: createPullRequest}),
      ...(eventType && {event_type: eventType}),
      ...(eventContent && {event_content: eventContent}),
    },
  })
}

function createOptimisticTask({
  clientGeneratedId,
  nwo,
  problemStatement,
  customAgent,
  agentId,
}: CreateAPITaskMutationParams): OptimisticTask {
  return {
    resource_type: 'pull',
    resource_id: -1, // Placeholder until we get real ID from server
    resource_global_id: '', // Placeholder until we get real ID from server
    session_count: 1,
    latest_session_updated_at: new Date().toISOString(),
    latest_session_state: 'in_progress',
    isOptimistic: true,
    clientGeneratedId,
    nwo,
    problemStatement,
    name: problemStatement,
    custom_agent: customAgent,
    agentId: agentId?.toString(),
  }
}
